/**
 * <ListBadge> — compact chip for `lx_list` / `lx_search` / `web_search` /
 * `web_fetch`.
 *
 * These had no dedicated renderer, so they fell through to `CollapsedToolJson`
 * and printed a raw argument dump in the middle of the conversation:
 *
 *     ▸ TOOL · LX_LIST
 *       { "path": "artifacts" }
 *
 * Operator, 2026-08-13, on the sequential activity log: *"simple output to a
 * terminal window"*. A JSON block is dev-facing next to the clean file chips
 * beside it, and it is three lines tall for one fact. This says the same thing
 * in one chip and matches `ReadBadge`/`OpenedFileBadge` exactly.
 *
 * Args only, never results (D5) — and the server already strips results from
 * the tool payload unless the session sets `verbose=full`, so there is nothing
 * to leak here even by accident.
 */
import { ArrowRightLeft, FolderSearch, Globe, HelpCircle, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

/** Per-tool chip identity: which icon, which noun, which arg is the subject.
 *  One table so a new tool is one row, not four scattered conditionals. */
const CHIP: Record<
  string,
  { icon: typeof Search; labelKey: string; testid: string; args: string[] }
> = {
  lx_list: {
    icon: FolderSearch,
    labelKey: "chat.activity.trailLabel.lx_list",
    testid: "list-badge",
    args: ["path", "dir", "folder"],
  },
  lx_search: {
    icon: Search,
    labelKey: "chat.activity.trailLabel.lx_search",
    testid: "search-badge",
    args: ["query", "q", "text"],
  },
  // S7 — the concierge's web tools. The QUERY/URL are args and render (D5:
  // results never reach the SPA at all). Names verified against the upstream
  // registrations; see lexide-web-tools-activity-miniplan.md §1.
  web_search: {
    icon: Search,
    labelKey: "chat.activity.trailLabel.web_search",
    testid: "web-search-badge",
    args: ["query", "q", "text"],
  },
  web_fetch: {
    icon: Globe,
    labelKey: "chat.activity.trailLabel.web_fetch",
    testid: "web-fetch-badge",
    args: ["url"],
  },
  // lx_help's arg is the lawyer's OWN QUESTION (`query`, lextools
  // lx-help.ts:40) — `args: []` means the chip can never render it, which is
  // the whole point (operator directive 2026-08-14: the raw row duplicated the
  // lawyer's message verbatim one line under their bubble).
  lx_help: {
    icon: HelpCircle,
    labelKey: "chat.activity.trailLabel.lx_help",
    testid: "help-badge",
    args: [],
  },
  lx_move_resource: {
    icon: ArrowRightLeft,
    labelKey: "chat.activity.trailLabel.lx_move_resource",
    testid: "move-badge",
    args: ["to", "path"],
  },
};

export function ListBadge({ block }: { block: ToolUseBlockType }) {
  const { t } = useTranslation();
  const chip = CHIP[block.name] ?? CHIP.lx_list;
  const input = (block.input ?? {}) as Record<string, unknown>;
  let value: string | null = null;
  for (const k of chip.args) {
    const v = input[k];
    if (typeof v === "string" && v.trim()) {
      value = v.trim();
      break;
    }
  }
  const Icon = chip.icon;
  return (
    <div
      data-testid={chip.testid}
      className="my-1 inline-flex max-w-full items-center gap-1.5 rounded border border-cream-300 bg-cream-100/30 px-2 py-1 text-caption text-coffee-700 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300"
    >
      <Icon size={12} className="shrink-0" />
      <span>{t(chip.labelKey)}</span>
      {value && <span className="min-w-0 truncate font-mono" title={value}>{value}</span>}
    </div>
  );
}
